<template>
  <article
    class="tarjeta"
    @click="$emit('abrir', ruta)"
  >
    <div class="fotos">
      <div class="marco-portada">
        <!-- PORTADA. El marco manda el alto, asi el spinner ocupa exactamente
             lo mismo que la foto y la tarjeta no pega un salto al cargar.

             eager: por defecto v-img no crea el <img> hasta que su
             IntersectionObserver dice que esta a la vista. Aqui eso no ahorra
             nada, porque el base64 YA se descargo antes de llegar a la
             plantilla: los bytes estan en memoria y aplazar el pintado solo
             agrega un motivo para que la foto no aparezca nunca. -->
        <div
          v-if="cargandoPortada"
          class="capa-carga"
        >
          <v-progress-circular
            indeterminate
            color="white"
            size="26"
            width="3"
          />
        </div>
        <v-img
          v-else
          :src="portada"
          :alt="`Foto de ${nombrePortada}`"
          cover
          eager
          height="100%"
          class="foto"
        >
          <!-- La promesa ya resolvio, pero al navegador todavia le falta pintar
               el base64. Sin esto se ve un hueco blanco en ese instante. -->
          <template #placeholder>
            <div class="capa-carga">
              <v-progress-circular
                indeterminate
                color="white"
                size="26"
                width="3"
              />
            </div>
          </template>
        </v-img>

        <!-- LAS DEMAS. Llegan en la segunda tanda y aparecen segun se resuelven.
             Van SOBRE la portada y no debajo: colgadas aparte dejaban un hueco
             blanco cuando solo habia una, y estiraban la columna de fotos hasta
             descuadrarla contra el texto. -->
        <div
          v-if="hayTira"
          class="tira"
        >
          <div
            v-for="mini in tiraVisible"
            :key="mini.id"
            class="mini"
          >
            <v-img
              v-if="mini.imagen"
              :src="mini.imagen"
              :alt="`Foto de ${mini.nombre}`"
              cover
              eager
              height="100%"
            />
            <div
              v-else
              class="mini-cargando"
            >
              <v-progress-circular
                indeterminate
                color="white"
                size="14"
                width="2"
              />
            </div>
          </div>
        </div>

        <div class="cinta">
          <v-icon
            size="15"
            color="white"
          >
            mdi-map-marker-path
          </v-icon>
          <span
            v-if="ruta.city"
            class="ciudad"
          >{{ ruta.city }}</span>
        </div>
      </div>
    </div>

    <div class="cuerpo">
      <h2 class="nombre">
        {{ ruta.name }}
      </h2>

      <div class="datos">
        <span class="dato">
          <v-icon size="14">mdi-map-marker-outline</v-icon>
          {{ ruta.visibleStops }} {{ ruta.visibleStops === 1 ? 'parada' : 'paradas' }}
        </span>
        <span
          v-if="ruta.estimatedMinutes"
          class="dato"
        >
          <v-icon size="14">mdi-clock-outline</v-icon>
          {{ duracion }}
        </span>
      </div>

      <p
        v-if="ruta.description"
        class="descripcion"
      >
        {{ ruta.description }}
      </p>

      <div class="pie">
        <!-- El recorrido en una linea: es lo que de verdad distingue una ruta
             de otra sin tener que abrirla -->
        <span
          v-if="nombresParadas"
          class="paradas"
        >{{ nombresParadas }}</span>
        <span class="ir">
          Ver el recorrido
          <v-icon size="16">mdi-arrow-right</v-icon>
        </span>
      </div>
    </div>
  </article>
</template>

<script>
import apisImagenes from '@/apis/apisImagenes'
import imagenPorDefecto from '@/assets/imagen-none.jpg'

/** Cuantas miniaturas caben en la esquina de la portada sin taparla. */
const MAX_TIRA = 3

/**
 * Tarjeta de una ruta en un listado.
 *
 * Recibe la ruta ya cargada y no sabe de navegacion: avisa hacia arriba con
 * "abrir". Asi sirve igual en la vista de Rutas y en cualquier otro sitio donde
 * se quieran mostrar rutas, sin arrastrar la logica de cada pantalla.
 *
 * LAS FOTOS LAS PIDE ELLA MISMA, Y EN DOS TANDAS:
 *
 *   1. la portada, que es la primera parada del recorrido. Una sola peticion,
 *      para que la tarjeta tenga imagen cuanto antes.
 *   2. las demas paradas, cada una por su lado y solo cuando la portada ya
 *      esta. Van apareciendo segun se resuelven.
 *
 * Separarlas importa: si se pidieran todas a la vez, la portada competiria por
 * turno con tres imagenes que no son lo primero que se mira, y la tarjeta
 * tardaria mas en dejar de estar vacia. Las rutas no tienen imagen propia; se
 * usan las de sus paradas, que ademas es honesto porque ensena lo que de verdad
 * hay en el camino.
 */
export default {
  name: 'TarjetaRuta',
  props: {
    ruta: {
      type: Object,
      required: true
    }
  },
  emits: ['abrir'],
  data() {
    return {
      imagenPortada: null,
      cargandoPortada: true,
      // { id, nombre, imagen, estado }. El hueco existe desde el principio,
      // asi la tira no da saltos segun van llegando las fotos.
      tira: []
    }
  },
  computed: {
    portadas() {
      return this.ruta.covers || []
    },
    /**
     * De donde salen las fotos de esta tarjeta.
     *
     * Si la ruta tiene imagenes propias -el afiche del festival- mandan esas y
     * las paradas no se usan: quien armo la ruta eligio con que se presenta.
     * Si no, se cae a las fotos de las paradas, que al menos enseñan algo que
     * de verdad esta en el recorrido.
     */
    usaImagenesPropias() {
      return this.ruta.ownImages === true
    },
    portada() {
      return this.imagenPortada || imagenPorDefecto
    },
    nombrePortada() {
      if (this.usaImagenesPropias || this.portadas.length === 0) {
        return this.ruta.name
      }
      return this.portadas[0].name
    },
    /**
     * Las que siguen en pie: cargando o ya con foto.
     *
     * Se filtra aqui y NO se saca del array: las peticiones estan en vuelo y
     * cada una escribe en su posicion, asi que mover el array a mitad de camino
     * hace que una respuesta caiga en la miniatura equivocada.
     */
    tiraVisible() {
      return this.tira.filter(m => m.estado !== 'sin')
    },
    hayTira() {
      return this.tiraVisible.length > 0
    },
    /** "Super Wings · Parque Simón Bolívar" */
    nombresParadas() {
      return this.portadas.map(p => p.name).join(' · ')
    },
    /** 135 -> "2 h 15 min" */
    duracion() {
      const minutos = this.ruta.estimatedMinutes

      if (!minutos) {
        return ''
      }
      if (minutos < 60) {
        return `${minutos} min`
      }

      const horas = Math.floor(minutos / 60)
      const resto = minutos % 60

      return resto === 0 ? `${horas} h` : `${horas} h ${resto} min`
    }
  },
  watch: {
    // Al cambiar de pagina la tarjeta se reutiliza con otra ruta
    'ruta.idRoute'() {
      this.imagenPortada = null
      this.tira = []
      this.cargarPortada()
    }
  },
  created() {
    this.cargarPortada()
  },
  methods: {
    /** Primera tanda: una sola imagen. */
    cargarPortada() {
      // Sin imagenes propias y sin paradas visibles no hay de donde sacar foto.
      // Se apaga el spinner igual: si no, la tarjeta gira para siempre.
      if (!this.usaImagenesPropias && this.portadas.length === 0) {
        this.cargandoPortada = false
        return
      }

      this.cargandoPortada = true

      const peticion = this.usaImagenesPropias
        ? apisImagenes.principal('ruta', this.ruta.idRoute).catch(() => null)
        : this.primeraImagenDe(this.portadas[0])

      peticion
        .then(imagen => {
          this.imagenPortada = imagen
        })
        .finally(() => {
          this.cargandoPortada = false
          // Encadenada, no en paralelo: el resto arranca cuando la portada ya
          // esta pintada.
          this.cargarResto()
        })
    },
    /** Segunda tanda: las demas, cada una a su ritmo. */
    cargarResto() {
      if (this.usaImagenesPropias) {
        this.cargarRestoPropias()
        return
      }

      const resto = this.portadas.slice(1)

      if (resto.length === 0) {
        return
      }

      this.tira = resto.map((p, i) => ({
        id: `${p.type}-${p.id}-${i}`,
        nombre: p.name,
        imagen: null,
        estado: 'cargando'
      }))

      resto.forEach((parada, indice) => {
        this.primeraImagenDe(parada)
          .then(imagen => {
            const mini = this.tira[indice]

            if (!mini) {
              return
            }
            if (imagen) {
              mini.imagen = imagen
              mini.estado = 'ok'
            } else {
              // Sin foto no se deja un hueco girando para siempre
              mini.estado = 'sin'
            }
          })
      })
    },
    /**
     * Las demas imagenes propias de la ruta. Una sola peticion, porque vienen
     * todas de la misma entidad; la portada ya se pidio aparte con limite 1.
     */
    cargarRestoPropias() {
      apisImagenes.resto('ruta', this.ruta.idRoute, MAX_TIRA)
        .then(fotos => {
          this.tira = (fotos || []).map((foto, i) => ({
            id: `ruta-${this.ruta.idRoute}-${i}`,
            nombre: this.ruta.name,
            imagen: foto,
            estado: 'ok'
          }))
        })
        .catch(() => {
          this.tira = []
        })
    },
    /**
     * La primera imagen de una empresa o de un lugar. Null si no tiene.
     *
     * Con limite=1 y no con el endpoint de siempre: aquel devuelve TODAS las
     * imagenes de la entidad, asi que una tarjeta con cuatro portadas terminaba
     * descargando quince fotos en base64 para enseñar cuatro.
     */
    primeraImagenDe(parada) {
      return apisImagenes.principal(parada.type, parada.id)
        // Que una foto falle no es motivo para romper la tarjeta
        .catch(() => null)
    }
  }
}
</script>

<style scoped>
/* Mobile first: la foto arriba y el texto debajo. En pantalla ancha van lado a
   lado, que es lo que deja leer la descripcion entera sin apretar la imagen. */
.tarjeta {
  display: flex;
  flex-direction: column;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.tarjeta:hover {
  transform: translateY(-2px);
  border-color: #1976d2;
  box-shadow: 0 6px 18px rgba(16, 42, 76, 0.12);
}

.fotos {
  min-width: 0;
}
.marco-portada {
  position: relative;
  aspect-ratio: 16 / 9;
  /* Sin esto el aspect-ratio se lo come el min-height: auto del flex */
  min-height: 0;
  background: #0d3f75;
}
.foto {
  width: 100%;
  height: 100%;
}

@media (min-width: 720px) {
  .tarjeta {
    flex-direction: row;
  }
  /* En fila el alto lo manda EL TEXTO y la foto rellena lo que haya.
     La portada va absoluta a proposito: dejandola en el flujo, su alto sale de
     la imagen (las fotos son cuadradas, asi que 300px de ancho estiraban la
     tarjeta a 300px de alto) y con height: 100% la referencia es circular,
     porque el alto de la fila depende de su contenido. Fuera del flujo no
     aporta alto: la fila mide lo que mide el texto y la foto se estira a eso. */
  .fotos {
    position: relative;
    flex: 0 0 300px;
  }
  .marco-portada {
    position: absolute;
    inset: 0;
    aspect-ratio: auto;
  }
  /* Suelo para que con una descripcion de una linea la foto no quede como una
     franja */
  .cuerpo {
    min-height: 172px;
  }
}

.capa-carga {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom right, #42a5f5 0%, #1976d2 45%, #0d3f75 100%);
}

/* Sobre la foto y no en una cabecera aparte: le deja todo el alto a la imagen
   y la ciudad se sigue leyendo gracias al degradado */
.cinta {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  gap: 6px;
  /* Sitio a la derecha para la tira de miniaturas */
  padding: 26px 74px 10px 10px;
  background: linear-gradient(to top, rgba(6, 26, 48, 0.85), rgba(6, 26, 48, 0));
}
.ciudad {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Las miniaturas van en la esquina de abajo, encima de la portada y sobre el
   mismo degradado que la ciudad: ocupan poco, no mueven nada al llegar y se
   leen como "esto tambien vas a ver".
   A 40px con borde blanco parecian cajas vacias en vez de fotos, de ahi los
   52 y el borde mas fino. */
.tira {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  gap: 5px;
}
.mini {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  border: 1.5px solid rgba(255, 255, 255, 0.92);
  background: rgba(6, 26, 48, 0.55);
  box-shadow: 0 2px 8px rgba(6, 26, 48, 0.45);
}
.mini-cargando {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cuerpo {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  min-width: 0;
}
.nombre {
  margin: 0;
  font-size: 1.14rem;
  font-weight: 700;
  line-height: 1.25;
  color: #1a1a1a;
  overflow-wrap: anywhere;
}

.datos {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.dato {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 9px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.74rem;
  font-weight: 600;
}

.descripcion {
  margin: 10px 0 0;
  font-size: 0.87rem;
  line-height: 1.5;
  color: #667085;
  /* Dos lineas y corta: las descripciones son de largo muy distinto y sin esto
     unas tarjetas quedan del doble de alto que otras */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pie {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  margin-top: auto;
  padding-top: 12px;
}
.paradas {
  min-width: 0;
  font-size: 0.78rem;
  color: #98a2b3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ir {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  color: #1976d2;
  font-size: 0.82rem;
  font-weight: 700;
}
</style>
